import type { TargetAndTransition } from 'motion';
import { Fragment, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useWindowSize, useOnClickOutside } from 'usehooks-ts';
import { AnimatePresence, motion } from 'motion/react';
import Dialog from '@/components/dialog';
import Month from '@/components/calendar/components/month';
import Day from '@/components/calendar/components/day';
import Select from '@/components/calendar/components/select';
import { useCalendarContext } from '@/components/calendar/context';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import '@/components/calendar/components/modal/modal-v2.css';
import '@/components/calendar/components/modal/modal-v1.css';

export default function Modal() {
  const {
    open,
    precision,
    modalTill,
    inputBorderRef,
    monthNames,
    yearList,
    monthIndex,
    yearIndex,
    closeButton,
    modalPosition,
    onOpenModal,
    onCloseModal,
    onCloseButton,
    onMonthIndexChange,
    onYearIndexChange,
    onOutsideClick,
  } = useCalendarContext();
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const { width } = useWindowSize();
  const modalRef = useRef<HTMLDivElement>(null);
  const attachedRef = useRef<HTMLDivElement>(null);
  const isDesktop = width >= modalTill;

  useOnClickOutside(modalRef, onOutsideClick);

  const modalContent = useMemo(() => {
    return (
      <div
        ref={modalRef}
        data-css-version={cssVersion}
        className={clsx('calendar-modal', {
          'calendar-modal--desktop': isDesktop,
          'calendar-modal--mobile': !isDesktop,
        })}
      >
        {precision === 'day' ? (
          <Fragment>
            <div className='calendar-modal__selects'>
              <Select name='month' value={monthIndex} options={monthNames} onChange={onMonthIndexChange} />
              <Select name='year' value={yearIndex} options={yearList} onChange={onYearIndexChange} />
            </div>
            <div className='calendar-modal__days'>
              <Day />
            </div>
          </Fragment>
        ) : precision === 'month' ? (
          <div style={{ maxWidth: inputBorderRef.current?.offsetWidth || 0 }} className='calendar-modal__months'>
            <Month />
          </div>
        ) : null}
        {cssVersion === 1 && (
          <div className='calendar-modal__footer'>
            <button className='calendar-modal__button' onClick={onCloseButton}>
              {closeButton}
            </button>
          </div>
        )}
      </div>
    );
  }, [
    isDesktop,
    precision,
    monthIndex,
    monthNames,
    onMonthIndexChange,
    yearIndex,
    yearList,
    onYearIndexChange,
    onCloseButton,
    closeButton,
  ]);

  return useMemo(() => {
    if (!isDesktop) {
      return (
        <Dialog on={open} size='content' position='center'>
          {modalContent}
        </Dialog>
      );
    }

    return (
      <AnimatePresence>
        {isDesktop && open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1, type: 'tween' }}
            ref={attachedRef}
            onAnimationComplete={(definition: TargetAndTransition) => {
              if (!attachedRef.current) return;
              const width = attachedRef.current.offsetWidth;
              const height = attachedRef.current.offsetHeight;
              definition.opacity ? onOpenModal({ width, height }) : onCloseModal();
            }}
            data-css-version={cssVersion}
            className={clsx('calendar-modal__attached', {
              'calendar-modal__attached--static': modalPosition === 'static',
              'calendar-modal__attached--absolute': modalPosition === 'absolute',
            })}
          >
            {modalContent}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }, [open, isDesktop, modalPosition, modalContent, attachedRef]);
}
